import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { sarApi, emailApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import useIdDocuments from '../../hooks/useIdDocuments';
import { Button } from '../ui/button';
import AppShell from '../AppShell';
import { SARStatusPill } from '../StatusPills';
import { EmailTimeline } from './index';
import StatusCard, { getSarStatus } from './StatusCard';
import SARStats from './SARStats';
import SAROutcomeFlags from './SAROutcomeFlags';
import HelpMeButton from './HelpMeButton';
import ICOGuidance from './ICOGuidance';
import LetterPreviewCard from './LetterPreviewCard';
import ReadyToSendCard from './ReadyToSendCard';
import IdDocumentsCard from './IdDocumentsCard';
import ReplyDialog from './ReplyDialog';
import PendingIdRequestsDialog from './PendingIdRequestsDialog';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const longDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

const itemDate = (e) => e.sent_at || e.received_at || e.created_at;

/**
 * SARDetail
 * ---------
 * One request. Progress comes first — four stat tiles (sent, deadline,
 * documents, correspondence), then the status sentence explaining them and
 * what happens next — because that's what someone actually wants to know
 * at a glance; the correspondence itself is real but secondary, further
 * down the page (no separate dated-milestones list: every milestone it
 * would have shown is already a message in that thread, with the deadline
 * tile already covering "sent"). Owns the request + thread data and the
 * send / reply actions; the ID-document flow lives in `useIdDocuments`.
 */
const SARDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();

  const [sar, setSar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [letterContent, setLetterContent] = useState('');

  const [emailThread, setEmailThread] = useState([]);
  const [deadline, setDeadline] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingId, setSendingId] = useState(false);

  const [showReplyDialog, setShowReplyDialog] = useState(false);
  const [replyToEmail, setReplyToEmail] = useState(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [replyTitle, setReplyTitle] = useState('');
  const [icoOpen, setIcoOpen] = useState(false);

  const idDocs = useIdDocuments({ currentSarId: id });
  const isDraft = sar?.status === 'draft';

  const loadSar = useCallback(async () => {
    try {
      const res = await sarApi.getOne(id);
      setSar(res.data);
      setLetterContent(res.data.letter_content);

      if (res.data.status !== 'draft') {
        try {
          const threadRes = await emailApi.getThread(id);
          setEmailThread(threadRes.data.emails || []);
          setDeadline(threadRes.data.deadline);
        } catch {
          console.log('No email thread yet');
        }
      }
    } catch {
      toast.error('Could not find that request.');
      navigate('/sar');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadSar();
  }, [loadSar]);

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      // Never attach ID to a plain "Send" click, initial or follow-up —
      // most organisations don't ask for it, and attaching it unasked was
      // shipping proof of identity to every recipient the moment the user
      // had it uploaded and permitted, with no warning either. ID only
      // goes out once an organisation actually asks: automatically, when
      // their reply is classified as an id_request (see routers/email.py),
      // or via the explicit "Send my ID now" / "send to all pending"
      // actions once that request shows up.
      await emailApi.sendSar(id, false);
      toast.success(`Sent to ${sar?.organisation_name || 'the organisation'}. They have 30 days to reply.`);
      await loadSar();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not send the request. Try again in a moment.');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendId = async (emailId) => {
    setSendingId(true);
    try {
      await emailApi.sendIdResponse(emailId);
      toast.success(`ID sent to ${sar?.organisation_name || 'the organisation'}.`);
      await loadSar();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not send your ID. Try again in a moment.');
    } finally {
      setSendingId(false);
    }
  };

  const sendReply = async (body) => {
    await emailApi.sendReply(id, replyToEmail.id, body);
    await loadSar();
  };

  const openReplyDialog = (email, { draft = '', title = '' } = {}) => {
    setReplyToEmail(email);
    setReplyDraft(draft);
    setReplyTitle(title);
    setShowReplyDialog(true);
  };

  const onReplyDialogChange = (open) => {
    setShowReplyDialog(open);
    if (!open) {
      setReplyToEmail(null);
      setReplyDraft('');
      setReplyTitle('');
    }
  };

  /**
   * "Send a reminder": a reply to their latest email (or to our original
   * request, which the server routes to the organisation) with a plain
   * chase already written. The user can change every word before sending.
   */
  const handleSendReminder = () => {
    const incoming = emailThread.filter((e) => e.direction === 'incoming');
    const latest = [...(incoming.length ? incoming : emailThread)]
      .sort((a, b) => new Date(itemDate(b)) - new Date(itemDate(a)))[0];
    if (!latest) {
      toast.error('There is nothing to reply to yet.');
      return;
    }
    const due = deadline?.extended_deadline || deadline?.initial_deadline;
    const late = typeof sar?.days_remaining === 'number' ? Math.abs(sar.days_remaining) : null;
    const draft = [
      'Dear Data Protection Officer,',
      '',
      `I sent you a subject access request on ${longDate(sar?.sent_at)}. The legal deadline to reply${due ? ` was ${longDate(due)}` : ' has passed'}${late ? `, ${late} day${late === 1 ? '' : 's'} ago` : ''}, and I have not received my records.`,
      '',
      'Please tell me when I will receive them. If I do not hear from you within 14 days I will complain to the Information Commissioner’s Office.',
      '',
      'Yours faithfully,',
      user?.full_name || '',
    ].join('\n');
    openReplyDialog(latest, { draft, title: 'Send a reminder' });
  };

  const scrollTo = (elementId) => {
    document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleShowIco = () => {
    setIcoOpen(true);
    // Let the steps render before scrolling to them.
    setTimeout(() => scrollTo('ico-guidance'), 50);
  };

  if (loading) {
    return (
      <AppShell>
        <main aria-busy="true">
          <p className="text-ink-55">Loading the request.</p>
        </main>
      </AppShell>
    );
  }

  const status = sar?.effective_status || sar?.status;
  const statusInfo = getSarStatus({
    sar,
    emailThread,
    deadline,
    hasIdDocuments: idDocs.hasIdDocuments,
    isReadyForIdRequests: idDocs.isReadyForIdRequests,
    onSendReminder: handleSendReminder,
    onShowIco: handleShowIco,
  });

  return (
    <AppShell>
      <main>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/sar')}
          className="-ml-2 mb-4"
          data-testid="back-to-sar-list-btn"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Requests
        </Button>

        <header className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="font-heading text-[34px] font-bold tracking-[-0.035em] leading-[1.05] text-ink">
              {sar?.organisation_name}
            </h1>
            {sar?.organisation_email && (
              <p className="pd-mono mt-2 text-ink-55 normal-case">{sar.organisation_email}</p>
            )}
          </div>
          <div className="shrink-0 sm:pt-2">
            <SARStatusPill status={status} daysRemaining={sar?.days_remaining} />
          </div>
        </header>

        {isDraft ? (
          <div className="max-w-[760px]">
            <StatusCard kind={statusInfo.kind} headline={statusInfo.headline} body={statusInfo.body} />
            <div className="mt-4">
              <ReadyToSendCard
                sar={sar}
                sending={sendingEmail}
                onSend={handleSendEmail}
                onEditOrganisation={() => navigate('/organisations')}
              />
            </div>
            <LetterPreviewCard letterContent={letterContent} />
          </div>
        ) : (
          <div className="space-y-7">
            {/* What matters at a glance, first: how it's progressing, not
                the correspondence itself — reading the thread is secondary
                to knowing where things stand. The numbers first, then the
                sentence explaining them and what happens next. */}
            <section className="space-y-4" data-testid="sar-timeline">
              <SARStats sar={sar} deadline={deadline} emailThread={emailThread} />
              <StatusCard
                kind={statusInfo.kind}
                headline={statusInfo.headline}
                body={statusInfo.body}
                actions={statusInfo.actions}
              />
            </section>

            {/* The correspondence itself — everything sent and received,
                with per-message notes in a fixed band on the right instead
                of a generic sidebar. */}
            <EmailTimeline
              emailThread={emailThread}
              sar={sar}
              deadline={deadline}
              onReply={(email) => openReplyDialog(email)}
              onRefresh={loadSar}
              hasIdDocuments={idDocs.hasIdDocuments}
              onSendId={handleSendId}
              onUploadId={() => scrollTo('id-documents')}
              sendingId={sendingId}
            />

            {!idDocs.isReadyForIdRequests && (
              <IdDocumentsCard
                idDocument={idDocs.idDocument}
                addressDocument={idDocs.addressDocument}
                uploadingId={idDocs.uploadingId}
                uploadingAddress={idDocs.uploadingAddress}
                idPermission={idDocs.idPermission}
                savingPermission={idDocs.savingPermission}
                onDocUpload={idDocs.handleDocUpload}
                onPermissionChange={idDocs.handlePermissionChange}
              />
            )}

            {/* Only renders when an ICO complaint is a realistic next step */}
            <ICOGuidance sar={sar} deadline={deadline} open={icoOpen} onOpenChange={setIcoOpen} />

            {/* Only renders once there is actually something to report */}
            <SAROutcomeFlags sar={sar} emailThread={emailThread} onUpdate={loadSar} />

            <HelpMeButton sar={sar} onUpdate={loadSar} />
          </div>
        )}
      </main>

      <ReplyDialog
        open={showReplyDialog}
        onOpenChange={onReplyDialogChange}
        email={replyToEmail}
        onSend={sendReply}
        initialBody={replyDraft}
        title={replyTitle}
      />
      <PendingIdRequestsDialog
        open={idDocs.showPendingIdDialog}
        onOpenChange={idDocs.setShowPendingIdDialog}
        requests={idDocs.pendingIdRequests}
        sending={idDocs.sendingToAll}
        onSendToAll={idDocs.handleSendIdToAll}
      />
    </AppShell>
  );
};

export default SARDetail;
